import {
    useCallback,
    useContext,
    useEffect,
    useRef,
    FC,
    ReactNode,
    RefObject,
    MouseEventHandler,
    ComponentProps,
} from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import scrollIntoView from 'scroll-into-view';
import { Link } from 'dibs-elements/exports/Link';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { ServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { PdpRespSimilarItemCarousel } from '../../PdpRespSimilarItems/PdpRespSimilarItemCarousel/PdpRespSimilarItemCarousel';
import {
    trackItemsDisplayed,
    trackItemClick,
    trackEcommerceLandingItemClick,
    trackLandingViewAllClick,
    trackLandingItemImpressions,
} from '../../tracking/similarItemsTracking';

import { PdpRespTopYMAL_similarItems$key } from './__generated__/PdpRespTopYMAL_similarItems.graphql';
import { PdpRespTopYMAL_item$key } from './__generated__/PdpRespTopYMAL_item.graphql';

import styles from './PdpRespTopYMAL.scss';

// Grid only shows 5 items per row on tablet, so use only a number that divides evenly in those cases
const MAX_TABLET_ITEMS = 10;

const itemFragment = graphql`
    fragment PdpRespTopYMAL_item on Item {
        ...PdpRespSimilarItemCarousel_baseItem
    }
`;

const similarItemsFragment = graphql`
    fragment PdpRespTopYMAL_similarItems on moreLikeThisItemConnection {
        edges {
            node {
                serviceId
                categoryPath
                categoryCode
                browseUrl
                title
                ecommerceTrackingParams
                ...PdpRespSimilarItemCarousel_item
            }
        }
    }
`;

export const PdpRespTopYMAL: FC<{
    item: PdpRespTopYMAL_item$key;
    similarItems: PdpRespTopYMAL_similarItems$key;
    scrollToRef: RefObject<HTMLDivElement>;
    currency: string;
}> = ({ scrollToRef, currency, ...props }) => {
    const similarItems = useFragment(similarItemsFragment, props.similarItems);
    const baseItem = useFragment(itemFragment, props.item);
    const { isTablet } = useContext(ServerVarsContext);

    const ymalItems = flattenConnectionNodes(similarItems);
    const itemsToShow = isTablet ? ymalItems.slice(0, MAX_TABLET_ITEMS) : ymalItems;

    const onViewMoreClick: MouseEventHandler = useCallback(() => {
        const elementRef = scrollToRef?.current;
        if (elementRef) {
            scrollIntoView(elementRef, { time: 500, align: { top: 0, topOffset: 100 } }, () =>
                elementRef.focus()
            );
        }

        trackLandingViewAllClick();
    }, [scrollToRef]);

    const onVisibilityChange: ComponentProps<typeof VisibilityTracker>['onVisibilityChange'] =
        useCallback(({ isVisible }) => {
            if (isVisible) {
                trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS' });
            }
        }, []);

    const onItemClick = useCallback(
        (index: number): void => {
            const item = similarItems.edges?.[index];

            trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS' });
            trackEcommerceLandingItemClick({
                index,
                landingItem: item,
                isSoldPdp: true, //TODO: don't hardcode ????
            });
        },
        [similarItems.edges]
    );

    const impressionsFired = useRef(false);
    useEffect(() => {
        const items = similarItems.edges;

        if (items?.length && !impressionsFired.current) {
            trackLandingItemImpressions(items, true); //TODO: don't hardcode ????
            impressionsFired.current = true;
        }
    }, [similarItems.edges]);

    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={containerRef} data-tn="pdp-view-similar-items">
            <VisibilityTracker elementRef={containerRef} onVisibilityChange={onVisibilityChange} />
            <div className={styles.headerContainer} data-tn="view-similar-items-header">
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.header}>
                            <FormattedMessage
                                id="PdpRespTopYMAL.viewSimilarItems"
                                defaultMessage="View Similar Items"
                            />
                        </Heading>
                    )}
                </HeadingLevel>

                <Link
                    className={styles.viewMore}
                    onClick={onViewMoreClick}
                    dataTn="view-more-similar-items"
                >
                    <FormattedMessage id="PdpRespTopYMAL.viewMore" defaultMessage="View More" />
                </Link>
            </div>

            <div className={styles.itemsContainer}>
                {itemsToShow.reduce<ReactNode[]>(
                    (acc, item, index) =>
                        item?.serviceId
                            ? acc.concat(
                                  <PdpRespSimilarItemCarousel
                                      key={item.serviceId}
                                      index={index}
                                      baseItem={baseItem}
                                      item={item}
                                      isCondensed={false}
                                      onClick={onItemClick}
                                      currency={currency}
                                      dataTn="pdp-similar-item"
                                  />
                              )
                            : acc,
                    []
                )}
            </div>
        </div>
    );
};
